import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {NgbCollapse} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin, map, Observable, Subscription} from 'rxjs';
import {UtilsService} from '@services/utils/utils.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {NotificationService} from '@services/notification/notification.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {DesignService} from '@services/left-menu/design/design.service';
import {StorageService} from '@services/storage/storage.service';
import {ToggleFavoriteService} from '@services/left-menu/favorites/toggle-favorite/toggle-favorite.service';
import {EditMenuPageComponent} from '@components/sidebar/modal/edit-menu-page/edit-menu-page.component';
import {MenuItem} from '@models/sidebar/menu-item';
import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';
import {MenuItemIcon} from '@models/sidebar/menu-item-icon';
import {UserMenuItem} from '@models/sidebar/response/user-menu-item';
import {PageItem} from '@models/sidebar/page-item';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {SelectPageService} from '@services/left-menu/select-page/select-page.service';
import {MenuOverviewService} from '@services/left-menu/menu-overview/menu-overview.service';
import {ExternalUrlService} from '@services/left-menu/page/external-url-page/external-url.service';
import {FramePageService} from '@services/left-menu/page/frame-page/frame-page.service';
import {UpdateUrlService} from '@services/update-url/update-url.service';
import {BreadcrumbService} from '@services/breadcrumb/breadcrumb.service';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {ModalService} from '@services/modal/modal.service';
import {EnvironmentConfig} from '@environments/environment';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {ElasticMenuItem} from '@models/elasticSearch/menu/elastic-menu-item';

@Component({
    selector: 'scitis-menu-overview',
    templateUrl: './menu-overview.component.html',
    styleUrls: ['./menu-overview.component.scss'],
    standalone: false
})
export class MenuOverviewComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  isAdminOrHigher: boolean = false;

  @Input()
  isHigherThanAdmin: boolean = false;

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  firebaseUserId: string = '';

  @Input()
  whiteListedMachineryGroupsMap: { [key: number]: boolean } | null = null;

  @Input()
  whiteListedMachineryGroups: number[] | null = null;

  @Output()
  expand: EventEmitter<boolean> = new EventEmitter<boolean>();

  isOverviewCollapsed: boolean = false;
  rootMenuItem: MenuItem = new MenuItem();
  actualRootMenuItem: MenuItem | undefined = undefined;
  childMenuItems: MenuItem[] = [];
  selectedChildMenuItem: MenuItem | undefined = undefined;
  currentPageNumber: number = 1;
  numberOfItemsPerPage: number = 20;
  childMenuItemsToDisplay: MenuItem[] = [];
  baseMenuItems: MenuItem[] = [];
  userMenuItems: MenuItem[] = [];
  nodeUserMenuItems: MenuItem[] = [];
  showNodeUserMenuItems = false;
  expandedNodeId: string | null = null;
  expandedNode: MenuItem | undefined = undefined;
  expandNodeInProgress = false;
  subscription: Subscription = new Subscription();
  clickOnDashboardSubscription: Subscription = new Subscription();
  clickOnBreadcrumbSubscription: Subscription = new Subscription();
  redirectToInteractiveDataAnalysisSubscription: Subscription = new Subscription();
  machinePageSelectionSubscription: Subscription = new Subscription();
  updateUrlSubscription: Subscription = new Subscription();
  hierarchyIsLoading = true;
  folderIsEmpty = false;
  editFolderTooltip = this.translation.stream('Edit');
  interactiveDataAnalysisMenuItem: MenuItem | undefined;
  isLoadingMenuItemParent: boolean = false;

  private CURRENT_PAGE_NAME = 'MenuOverview';
  private errorLoadingRootMenu = '';
  private errorGettingMenuItems = '';
  private ROOT_MENU_KEY = '';
  private SELECTED_NODE_KEY = '';
  private SELECTED_PAGE_KEY = '';
  private nodeFunctionalityEnabled: boolean = EnvironmentConfig.PortalSettings.nodeFunctionalityEnabled || false;


  constructor(
      private modalService: ModalService,
      private utilsService: UtilsService,
      private leftMenuService: LeftMenuService,
      private notificationService: NotificationService,
      private translation: TranslateService,
      private menuNotificationService: MenuNotificationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private designService: DesignService,
      private storageService: StorageService,
      private toggleFavoriteService: ToggleFavoriteService,
      private selectPageService: SelectPageService,
      private menuOverviewService: MenuOverviewService,
      private externalUrlService: ExternalUrlService,
      private framePageService: FramePageService,
      private updateUrlService: UpdateUrlService,
      private elasticSearchService: ElasticSearchService,
      private breadcrumbService: BreadcrumbService
  ) {
    this.setTranslations();
  }

  get moreItemsAreAvailable(): boolean {
    return this.currentPageNumber * this.numberOfItemsPerPage < this.childMenuItems.length;
  }

  get displayRootMenu(): boolean {
    return this.selectedChildMenuItem === undefined;
  }

  get displayEditFolderButton(): boolean {
    return this.menuOverviewService.displayEditFolderButton(
        this.selectedChildMenuItem,
        this.hierarchyIsLoading,
        this.userRole
    );
  }

  get parentTypeCategory(): string {
    return this.menuOverviewService.parentTypeCategory(this.selectedChildMenuItem);
  }

  get folderTooltip(): Observable<string> {
    return this.editFolderTooltip.pipe(map(translation => translation + ' ' + this.selectedChildMenuItem?.name));
  }

  get rootFolderTooltip(): Observable<string> {
    return this.editFolderTooltip.pipe(map(translation => translation + ' ' + this.rootMenuItem?.name));
  }

  get backButtonClass(): string {
    return this.menuOverviewService.backButtonClass(this.selectedChildMenuItem, this.actualRootMenuItem);
  }

  get rootMenuKeyIsValid(): boolean {
    return this.firebaseUserId !== '' && this.ROOT_MENU_KEY.endsWith(this.firebaseUserId);
  }

  public getId(menuItem: MenuItem): string | null {
    return this.menuOverviewService.getId(menuItem);
  }

  static shouldUpdateName(message: MenuNotification): boolean {
    return message.operation === OperationType.UPDATE_NAME;
  }

  ngOnInit() {
    this.setStorageKeys();
    this.listenMenuNotifications();
    this.listenClickOnDashboard();
    this.listenClickOnBreadcrumb();
    this.listenRedirectToInteractiveDataAnalysis();
    this.listenMachinePageSelection();
    this.listenRedirectToGroupStatusPage();
    this.listenForUpdatedUrl();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.firebaseUserId && changes.firebaseUserId.currentValue && !changes.firebaseUserId.currentValue.isNullOrWhitespace()) {
      this.setStorageKeys();
      if (this.actualRootMenuItem) {
        this.setMenu();
      } else {
        this.leftMenuService.getRootMenu().subscribe((rootMenuItem: BaseMenuItem) => {
          this.actualRootMenuItem = Object.assign(new MenuItem(), rootMenuItem);
          this.actualRootMenuItem.icon = MenuItemIcon.ROOT_FOLDER;
          this.setMenu(false);
        });
      }
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.clickOnDashboardSubscription.unsubscribe();
    this.clickOnBreadcrumbSubscription.unsubscribe();
    this.redirectToInteractiveDataAnalysisSubscription.unsubscribe();
    this.machinePageSelectionSubscription.unsubscribe();
    this.updateUrlSubscription.unsubscribe();
  }

  toggleOverview(overviewCollapse: NgbCollapse): void {
    overviewCollapse.toggle();
    if (!overviewCollapse.collapsed) {
      this.expand.emit(true);
    }
  }

  open(event: any, selectedMenuItem: MenuItem) {
    event.stopPropagation();
    if (selectedMenuItem) {
      const modalRef = this.modalService.open(EditMenuPageComponent, {size: 'lg', ariaLabelledBy: 'modal-basic-title'});
      modalRef.componentInstance.menuItem = selectedMenuItem;
      modalRef.componentInstance.baseMenuItems = this.getPageItems(this.baseMenuItems, false, selectedMenuItem).filter((item) => item.type != MenuItemType.MACHINE_GROUP_STATUS_PAGE);
      modalRef.componentInstance.userMenuItems = this.getPageItems(this.userMenuItems, true, selectedMenuItem);
      modalRef.componentInstance.firebaseUserId = this.firebaseUserId;
      modalRef.componentInstance.isAdminOrHigher = this.isAdminOrHigher;
      modalRef.componentInstance.isHigherThanAdmin = this.isHigherThanAdmin;
      modalRef.result.then().catch(() => {
      });
    }
  }

  resetExpandedNode(): void {
    this.expandedNode = undefined;
    this.expandedNodeId = null;
  }

  onMenuItemSelected(selectedMenuItem: MenuItem) {
    if (selectedMenuItem.menuItemParent) {
      this.getPathRecursive(selectedMenuItem.menuItemParent);
    } else {
      this.getPathRecursive(selectedMenuItem);
    }
    this.setSelectedPageId(selectedMenuItem.id);
    this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, selectedMenuItem.id);
    if (selectedMenuItem.type === MenuItemType.MACHINE_GROUP || selectedMenuItem.type === MenuItemType.MACHINE) {
      this.slideIn(selectedMenuItem, false);
    } else if (selectedMenuItem.type === MenuItemType.NODE) {
      if (this.sameNodeIsClicked(selectedMenuItem)) {
        this.closeNode();
      } else {
        this.openNode(selectedMenuItem);
      }
    }
  }

  onMenuItemSelectedFromOutside(selectedMenuItem: MenuItem) {
    if (selectedMenuItem.menuItemParent) {
      this.getPathRecursive(selectedMenuItem.menuItemParent);
    } else {
      this.getPathRecursive(selectedMenuItem);
    }

    this.storageService.writeValueToIndexedDb(this.ROOT_MENU_KEY, '');
    this.storageService.writeValueToIndexedDb(this.SELECTED_NODE_KEY, '');
    this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, '');

    if (selectedMenuItem.type === MenuItemType.MACHINE_GROUP || selectedMenuItem.type === MenuItemType.MACHINE) {
      this.slideIn(selectedMenuItem, true);
    } else if (selectedMenuItem.type === MenuItemType.NODE) {
      this.openFavoriteNode(selectedMenuItem);
    } else {
      if (selectedMenuItem.nodeId) {
        this.openPageInsideNode(selectedMenuItem);
      } else {
        this.openPageInsideMachine(selectedMenuItem);
      }
    }
  }

  sameNodeIsClicked(selectedMenuItem: MenuItem): boolean {
    return String(this.expandedNodeId) === String(selectedMenuItem.id);
  }

  closeNode(): void {
    this.expandedNodeId = null;
    this.expandedNode = undefined;
    this.expandNodeInProgress = false;
    this.nodeUserMenuItems = [];
    this.setSelectedPageId(null);
    this.storageService.writeValueToIndexedDb(this.SELECTED_NODE_KEY, '');
    this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, '');
  }

  openNode(selectedMenuItem: MenuItem): void {
    this.storageService.writeValueToIndexedDb(this.SELECTED_NODE_KEY, selectedMenuItem);
    this.expandedNodeId = selectedMenuItem.id;
    this.expandedNode = selectedMenuItem;
    this.expandNodeInProgress = true;
    this.displayNodeDetails(selectedMenuItem);
  }

  openFavoriteNode(selectedMenuItem: MenuItem): void {
    this.storageService.writeValueToIndexedDb(this.SELECTED_NODE_KEY, selectedMenuItem);
    this.expandedNodeId = selectedMenuItem.id;
    this.expandedNode = selectedMenuItem;
    this.expandNodeInProgress = true;
    this.setMachineAndExpandNode(selectedMenuItem);
  }

  goToParent(): void {
    if (!this.menuOverviewService.hasParent(this.selectedChildMenuItem, this.actualRootMenuItem)) {
      this.storageService.writeValueToIndexedDb(this.ROOT_MENU_KEY, this.actualRootMenuItem);
      return;
    }
    this.showNodeUserMenuItems = false;
    this.expandedNodeId = null;
    this.storageService.writeValueToIndexedDb(this.ROOT_MENU_KEY, this.rootMenuItem);
    if (String(this.selectedChildMenuItem?.parentId) === String(this.rootMenuItem.id)) {
      this.storageService.writeValueToIndexedDb('activeGroupName', this.rootMenuItem.name);
      const commands = this.leftMenuService.getCommands(this.rootMenuItem, this.firebaseUserId, undefined);
      this.selectedChildMenuItem = undefined;
      this.setChildren(this.rootMenuItem);
      this.generateAndBroadcastRoutes(this.rootMenuItem);
      this.isLoadingMenuItemParent = false;
      this.leftMenuService.navigateToPage(commands);
      return;
    }

    const menuParentId = this.getParentMenuItemId(this.selectedChildMenuItem);
    if (menuParentId === this.rootMenuItem.id) {
      this.setParent(this.rootMenuItem);
      this.resetPageSettings();
      return;
    }


    this.isLoadingMenuItemParent = true;
    this.elasticSearchService.getBaseMenuItemById(menuParentId, Number(this.rootMenuItem.id), this.whiteListedMachineryGroups, this.userRole.isSuperAdminRole).subscribe({
      next: result => {
        const parentMenuItem = result?.result?.data[0];
        parentMenuItem.disabled = this.whiteListedMachineryGroupsMap?.[parentMenuItem.id] == false;
        this.setParent(result?.result?.data[0]);
        this.isLoadingMenuItemParent = false;
        this.resetPageSettings();
      },
      error: error => {
        this.isLoadingMenuItemParent = false;
        console.error(error);
      }
    });
  }

  private getParentMenuItemId(currentMenuItem: MenuItem | undefined): string | null {
    if (!currentMenuItem) {
      return null;
    }

    if (currentMenuItem.parentId) {
      return currentMenuItem.parentId;
    }

    if (currentMenuItem.menuItemParent?.id) {
      return currentMenuItem.menuItemParent.id;
    }
    const menu: ElasticMenuItem = currentMenuItem as ElasticMenuItem;
    if (menu.parentMenuItem?.id) {
      return menu.parentMenuItem.id;
    }
    return null;
  }

  loadMoreChildren(): void {
    this.currentPageNumber++;
    this.setChildMenuItemsToDisplay();
  }

  setChildren(parentMenuItem: MenuItem, displayFirstPage = true, selectedPageId: string | null = null): void {
    this.folderIsEmpty = false;
    const observables = this.getObservables(parentMenuItem);
    forkJoin(observables).subscribe({
      next: results => {
        if (this.selectedChildMenuItem && String(this.selectedChildMenuItem.id) !== String(parentMenuItem.id)) {
          return;
        }
        this.userMenuItems = [];
        if (results) {
          if (results.length > 0) {
            this.setBaseMenuItems(parentMenuItem, results[0]);
          }
          if (results.length > 1) {
            this.setUserMenuItems(parentMenuItem, results[1]);
          }
          this.setChildMenuItemsToDisplay();
          if (this.expandedNode) {
            this.setNodeChildren(this.expandedNode, false);
          }

          if (selectedPageId) {
            this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, selectedPageId);
            this.setSelectedPageId(selectedPageId);
          } else if (displayFirstPage) {
            this.displayFirstPage(parentMenuItem);
          }
        }
        this.hierarchyIsLoading = false;
      }, error: error => {
        this.hierarchyIsLoading = false;
        this.notificationService.error(this.errorGettingMenuItems, error, this.CURRENT_PAGE_NAME, true);
      }
    });
  }

  setChildrenForFavoriteItem(parentMenuItem: MenuItem): void {
    this.folderIsEmpty = false;
    const observables = this.getObservables(parentMenuItem);
    forkJoin(observables).subscribe({
      next: results => {
        this.userMenuItems = [];
        if (results) {
          if (results.length > 0) {
            this.setBaseMenuItems(parentMenuItem, results[0]);
          }
          if (results.length > 1) {
            this.setUserMenuItems(parentMenuItem, results[1]);
          }
          this.displayPage(parentMenuItem);
          this.setChildMenuItemsToDisplay();
          this.checkIfFolderIsEmpty();
          if (this.expandedNode) {
            this.setNodeChildren(this.expandedNode, false);
          }
        }
        this.hierarchyIsLoading = false;
      }, error: error => {
        this.hierarchyIsLoading = false;
        this.notificationService.error(this.errorGettingMenuItems, error, this.CURRENT_PAGE_NAME, true);
      }
    });
  }

  private displayPage(parentMenuItem: MenuItem) {
    if (parentMenuItem.type === MenuItemType.MACHINE_GROUP) {
      const commands = this.leftMenuService.getCommands(parentMenuItem, this.firebaseUserId, undefined);
      this.leftMenuService.navigateToPage(commands);
    } else {
      this.displayFirstPage(parentMenuItem);
    }
  }

  setNodeChildren(nodeMenuItem: MenuItem, showFirstItem: boolean): void {
    if (nodeMenuItem.id) {
      this.leftMenuService.getUserMenuItems(nodeMenuItem.id, nodeMenuItem.isMachine).subscribe({
        next: results => {
          this.setNodeUserMenuItems(nodeMenuItem, results);
          this.showNodeUserMenuItems = true;
          this.expandNodeInProgress = false;
          if (this.nodeUserMenuItems.length > 0 && showFirstItem) {
            const childCommands = this.leftMenuService.getCommands(this.nodeUserMenuItems[0], this.firebaseUserId, this.interactiveDataAnalysisMenuItem);
            this.selectPageService.setSelectedPageId(this.nodeUserMenuItems[0].id);
            this.leftMenuService.navigateToPage(childCommands);
          }
        }, error: error => {
          this.notificationService.error(this.errorGettingMenuItems, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    }
  }

  onRootGroupSelected(): void {
    this.setMenu();
  }

  checkIfFolderIsEmpty(): void {
    this.folderIsEmpty = this.childMenuItems.length === 0;
  }

  resetPageSettings(): void {
    this.currentPageNumber = 1;
    this.numberOfItemsPerPage = 20;
  }

  setChildMenuItemsToDisplay(): void {
    this.childMenuItemsToDisplay = this.childMenuItems.filter((menuItem) => menuItem.type != MenuItemType.MACHINE_GROUP_STATUS_PAGE).slice(0, this.currentPageNumber * this.numberOfItemsPerPage);
    this.hideMenuItemsForLowerUsers();
    this.setupNodesVisibility();
    this.setFavoriteProperties(this.childMenuItemsToDisplay);
    this.childMenuItemsToDisplay.forEach(m => m.favoriteToggle = true);
    this.openPageSavedInStorage();
  }

  hideMenuItemsForLowerUsers(): void {
    if (!this.isAdminOrHigher && !this.userRole.isAnalyticsRole) {
      this.childMenuItemsToDisplay = this.childMenuItemsToDisplay.filter((menuItem) => menuItem.type !== MenuItemType.TABLEAU_PAGE);
    }

    if (this.userRole.isViewerRole) {
      const currentRootItem = this.selectedChildMenuItem ? this.selectedChildMenuItem : this.rootMenuItem;
      if (currentRootItem?.type === MenuItemType.MACHINE_GROUP) {
        this.childMenuItemsToDisplay = this.childMenuItemsToDisplay.filter(({type}) => {
          return type !== MenuItemType.MACHINE_GROUP_STATUS_PAGE && type !== MenuItemType.EXTERNAL_URL &&
              type !== MenuItemType.FRAME_PAGE && type !== MenuItemType.TABLEAU_PAGE && type !== MenuItemType.NODE;
        });
      }
      if (currentRootItem?.type === MenuItemType.MACHINE) {
        this.childMenuItemsToDisplay = this.childMenuItemsToDisplay.filter(({type}) => {
          return type !== MenuItemType.USER_DEFINED_PAGE
              && type !== MenuItemType.TABLEAU_PAGE && type !== MenuItemType.NODE;
        });
      }
    }

    this.folderIsEmpty = !this.childMenuItemsToDisplay?.length;
  }

  setupNodesVisibility() {
    this.childMenuItemsToDisplay = !this.nodeFunctionalityEnabled
        ? this.childMenuItemsToDisplay.filter((menuItem) => menuItem.type !== MenuItemType.NODE)
        : this.childMenuItemsToDisplay;
  }

  setMenu(displayFirstPage = true): void {
    this.hierarchyIsLoading = true;
    this.childMenuItemsToDisplay = [];
    this.storageService.readValueFromIndexedDb<string>(this.ROOT_MENU_KEY).then(
        (value) => {
          if (value !== null && undefined !== value && JSON.parse(value) !== '') {
            this.rootMenuItem = Object.assign(new MenuItem(), JSON.parse(value));
            if (this.rootMenuItem.id) {
              this.selectedChildMenuItem = Object.assign(new MenuItem(), this.utilsService.getObjectDeepCopy(this.rootMenuItem));
              this.setChildren(this.rootMenuItem, displayFirstPage);
              this.generateAndBroadcastRoutes(this.rootMenuItem);
            } else {
              this.loadRootMenu();
            }
          } else {
            this.loadRootMenu();
          }
        },
        (error) => {
          console.log('Error', error);
        });
    this.openNodeSavedInStorage();
  }

  openNodeSavedInStorage(): void {
    this.storageService.readValueFromIndexedDb<string>(this.SELECTED_NODE_KEY).then(
        (value) => {
          if (value !== null && undefined !== value) {
            this.expandedNode = Object.assign(new MenuItem(), JSON.parse(value));
            if (this.expandedNode) {
              this.expandedNodeId = this.expandedNode.id;
            }
          }
        },
        (error) => {
          console.log('Error', error);
        });
  }

  openPageSavedInStorage(): void {
    this.storageService.readValueFromIndexedDb<string>(this.SELECTED_PAGE_KEY).then(
        (value) => {
          if (value !== null && undefined !== value) {
            this.setSelectedPageId(JSON.parse(value));
          }
        },
        (error) => {
          console.log('Error', error);
        });
  }

  loadRootMenu(): void {
    this.leftMenuService.getRootMenu().subscribe({
      next: (rootMenuItem: BaseMenuItem) => {
        this.rootMenuItem = Object.assign(new MenuItem(), rootMenuItem);
        this.rootMenuItem.icon = MenuItemIcon.ROOT_FOLDER;
        this.selectedChildMenuItem = this.rootMenuItem;
        this.actualRootMenuItem = this.rootMenuItem;
        this.generateAndBroadcastRoutes(this.rootMenuItem);
        this.setRootMenuToStorage(this.rootMenuItem);
        this.setChildren(this.rootMenuItem);
      }, error: error => {
        this.notificationService.error(this.errorLoadingRootMenu, error, this.CURRENT_PAGE_NAME, true);
      }
    });
  }

  getPageItems(menuItems: MenuItem[], setUserId: boolean, selectedMenuItem: MenuItem): PageItem[] {
    return this.menuOverviewService.getPageItems(menuItems, setUserId, selectedMenuItem, this.selectedChildMenuItem, this.isAdminOrHigher, this.firebaseUserId);
  }

  selectedMenuItemIsUpdated(message: MenuNotification): boolean {
    return this.menuOverviewService.selectedMenuItemIsUpdated(this.selectedChildMenuItem, message);
  }

  refreshMenu(message: MenuNotification): void {
    if (message.operation === OperationType.NONE) {
      return;
    }
    this.leftMenuService.resetCache();
    if (message.operation === OperationType.UPDATE_USER_MACHINERY_GROUP) {
      this.setRootMenuToStorage('');
      this.setMenu();
    } else {
      if (this.selectedChildMenuItem) {
        if (this.selectedMenuItemIsUpdated(message)) {
          this.updateMenuForSelectedMenuItem(message);
        } else {
          this.selectedChildMenuItem = undefined;
          this.setMenu();
        }
      } else {
        this.setMenu();
      }
    }
  }

  updateMenuForSelectedMenuItem(message: MenuNotification): void {
    if (this.selectedChildMenuItem) {
      if (MenuOverviewComponent.shouldUpdateName(message)) {
        this.selectedChildMenuItem.name = message.newName;
      } else if (message.operation !== OperationType.DELETE) {
        this.setChildren(this.selectedChildMenuItem, false);
      } else {
        this.selectedChildMenuItem = undefined;
        this.setRootMenuToStorage('');
        this.setMenu();
      }
    }
  }

  slideIn(selectedMenuItem: MenuItem, isFavorite: boolean): void {
    this.setRootMenuToStorage(selectedMenuItem);
    this.hierarchyIsLoading = true;
    this.childMenuItemsToDisplay = [];
    this.showNodeUserMenuItems = false;
    this.expandedNodeId = null;
    this.nodeUserMenuItems = [];
    this.selectedChildMenuItem = Object.assign(new MenuItem(), this.utilsService.getObjectDeepCopy(selectedMenuItem));
    this.selectedChildMenuItem.favoriteToggle = false;
    this.childMenuItems = [];
    this.generateAndBroadcastRoutes(selectedMenuItem);
    if (isFavorite) {
      this.setChildrenForFavoriteItem(this.selectedChildMenuItem);
    } else {
      this.setChildren(this.selectedChildMenuItem);
    }
  }

  displayNodeDetails(selectedMenuItem: MenuItem): void {
    this.nodeUserMenuItems = [];
    this.setNodeChildren(selectedMenuItem, true);
  }

  getClass(menuItem: MenuItem): string {
    let className = this.menuOverviewService.getClassForNode(menuItem, this.expandedNodeId);
    if (String(menuItem.id) === String(this.selectPageService.getSelectedPageId) && this.isExpandedNodeOrOtherType(menuItem)) {
      className += ' scitis-menu-item-active';
    }
    return className;
  }

  isExpandedNodeOrOtherType(menuItem: MenuItem): boolean {
    return this.menuOverviewService.isExpandedNodeOrOtherType(menuItem, this.expandedNodeId);
  }

  private getMenuItem(parentMenuItem: MenuItem, userMenuItem: UserMenuItem): MenuItem {
    return this.menuOverviewService.getMenuItem(parentMenuItem, userMenuItem);
  }

  private setTranslations(): void {
    this.errorLoadingRootMenu = this.translation.instant('Error loading root menu.');
    this.errorGettingMenuItems = this.translation.instant('Error loading menu items.');
  }

  private setUserMenuItems(parentMenuItem: MenuItem, userMenuItems: UserMenuItem[]): void {
    this.userMenuItems = [];
    for (const userMenuItem of userMenuItems) {
      const menuItem = this.getMenuItem(parentMenuItem, userMenuItem);
      this.userMenuItems.push(menuItem);
    }
    this.userMenuItems.sort((function (a, b) {
      return a.index - b.index;
    }));
    this.childMenuItems.push(...this.userMenuItems);
  }

  private setNodeUserMenuItems(parentMenuItem: MenuItem, userMenuItems: UserMenuItem[]): void {
    this.nodeUserMenuItems = [];
    if (userMenuItems && userMenuItems.length > 0) {
      for (const userMenuItem of userMenuItems) {
        const menuItem = this.getMenuItem(parentMenuItem, userMenuItem);
        this.nodeUserMenuItems.push(menuItem);
      }
      this.nodeUserMenuItems = this.tableHeaderSortableService.sortItems<MenuItem>(this.nodeUserMenuItems, 'index', 'asc');
      this.setFavoriteProperties(this.nodeUserMenuItems);
    }
  }

  private setFavoriteProperties(menuItems: MenuItem[]): void {
    menuItems.forEach(m => {
      if (m.id) {
        m.favoriteToggle = true;
        m.isFavorite = this.toggleFavoriteService.isFavorite(m.id);
      }
    });
  }

  private getObservables(parentMenuItem: MenuItem): Observable<any>[] {
    const observables = new Array<Observable<any>>();
    const id: string | null = this.getId(parentMenuItem);
    if (parentMenuItem && id && parentMenuItem.type) {
      observables.push(this.leftMenuService.getMenuItems(id, parentMenuItem.isMachineGroup));
    }
    if (parentMenuItem.machineId && parentMenuItem.isMachine) {
      observables.push(this.leftMenuService.getUserMenuItems(parentMenuItem.machineId, parentMenuItem.isMachine));
    }
    return observables;
  }

  private setBaseMenuItems(parentMenuItem: MenuItem, baseMenuItems: BaseMenuItem[]): void {
    this.childMenuItems = [];
    this.baseMenuItems = [];
    const map: Map<MenuItem, Observable<any>> = new Map<MenuItem, Observable<any>>();
    for (const baseMenuItem of baseMenuItems) {
      const menuItem = Object.assign(new MenuItem(), baseMenuItem);
      menuItem.icon = this.designService.getIcon(menuItem.type);
      menuItem.menuItemParent = parentMenuItem;
      this.childMenuItems.push(menuItem);
      if (menuItem.type === MenuItemType.EXTERNAL_URL || menuItem.type === MenuItemType.FRAME_PAGE) {
        const observable = this.getUrlObservable(menuItem);
        if (observable) {
          map.set(menuItem, observable);
        }
      }
      if (menuItem.type === MenuItemType.DATA_VISUALIZATION_PAGE) {
        this.interactiveDataAnalysisMenuItem = Object.assign(new MenuItem(), menuItem);
      }
    }

    this.childMenuItems.sort((function (a, b) {
      return a.index - b.index;
    }));
    this.baseMenuItems = [...this.childMenuItems];
    if (map.size > 0) {
      this.setUrl(map);
    }
  }

  private getUrlObservable(menuItem: MenuItem): Observable<any> | undefined {
    if (menuItem.parentId && menuItem.pageId) {
      const isParentMachineGroup = menuItem.menuItemParent?.type === MenuItemType.MACHINE_GROUP;
      if (menuItem.type === MenuItemType.EXTERNAL_URL) {
        return this.externalUrlService.getExternalUrl(menuItem.parentId, isParentMachineGroup, menuItem.pageId);
      }
      if (menuItem.type === MenuItemType.FRAME_PAGE) {
        return this.framePageService.getFramePage(menuItem.parentId, isParentMachineGroup, menuItem.pageId);
      }
    }
    return undefined;
  }

  private setUrl(map: Map<MenuItem, Observable<any>>): void {
    const observables: Observable<any>[] = [...map.values()];
    forkJoin(observables).subscribe(urlResults => {
      urlResults.forEach(urlResult => {
        this.baseMenuItems.forEach((baseMenuItem: BaseMenuItem) => {
          if (baseMenuItem.pageId === urlResult.id) {
            baseMenuItem.url = urlResult.url;
          }
        });
      });
    });
  }

  public setRootMenuToStorage(menuItem: MenuItem | string): void {
    if (this.rootMenuKeyIsValid) {
      this.storageService.writeValueToIndexedDb(this.ROOT_MENU_KEY, menuItem);
      this.resetSelectedPageOnStorage();
    }
  }

  private resetSelectedPageOnStorage(): void {
    this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, '');
  }

  private setStorageKeys(): void {
    this.ROOT_MENU_KEY = 'rootMenu' + this.firebaseUserId;
    this.SELECTED_NODE_KEY = 'selectedNode' + this.firebaseUserId;
    this.SELECTED_PAGE_KEY = 'selectedPage' + this.firebaseUserId;
  }

  private displayFirstPage(parentMenuItem: MenuItem): void {
    let menuItem: MenuItem | undefined = undefined;
    if (parentMenuItem.type === MenuItemType.MACHINE) {
      if (this.baseMenuItems.length > 0) {
        menuItem = this.baseMenuItems[0];
      } else if (this.userMenuItems.length > 0) {
        menuItem = this.userMenuItems[0];
      }
    } else if (parentMenuItem.type === MenuItemType.NODE && this.nodeUserMenuItems.length > 0) {
      menuItem = this.nodeUserMenuItems[0];
    }
    if (menuItem) {
      this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, menuItem.id).then(() => {
        this.setSelectedPageId(menuItem ? menuItem.id : null);
      });
      const commands = this.leftMenuService.getCommands(menuItem, this.firebaseUserId, this.interactiveDataAnalysisMenuItem);
      this.leftMenuService.navigateToPage(commands);
    }
  }

  private setSelectedPageId(value: string | null): void {
    this.selectPageService.setSelectedPageId(value);
  }

  private setParent(menuItemParent: any): void {
    if (this.selectedChildMenuItem) {
      this.selectedChildMenuItem.menuItemParent = Object.assign(new MenuItem(), menuItemParent);
      this.selectedChildMenuItem.parentId = menuItemParent.id;
      if (this.selectedChildMenuItem.menuItemParent) {
        this.getPathRecursive(this.selectedChildMenuItem.menuItemParent);
        this.selectedChildMenuItem.menuItemParent.icon = this.designService.getIcon(this.selectedChildMenuItem.menuItemParent.type);
        this.storageService.writeValueToIndexedDb('activeGroupName', this.selectedChildMenuItem.menuItemParent.name);
        const commands = this.leftMenuService.getCommands(this.selectedChildMenuItem.menuItemParent, this.firebaseUserId, undefined);
        this.onMenuItemSelected(this.selectedChildMenuItem.menuItemParent);
        this.leftMenuService.navigateToPage(commands);
      }
    }
  }

  private setMachineAndExpandNode(selectedMenuItem: MenuItem): void {
    this.hierarchyIsLoading = true;
    this.childMenuItemsToDisplay = [];
    if (!selectedMenuItem || !selectedMenuItem.menuItemParent || !selectedMenuItem.menuItemParent.id) {
      return;
    }
    this.leftMenuService.getParent(selectedMenuItem.menuItemParent.id, true).subscribe({
      next: menuItemParent => {
        this.selectedChildMenuItem = Object.assign(new MenuItem(), this.utilsService.getObjectDeepCopy(menuItemParent));
        this.expandNode(selectedMenuItem);
      }, error: error => {
        console.log(error);
        this.loadRootMenu();
      }
    });
  }

  private expandNode(selectedMenuItem: MenuItem): void {
    if (this.selectedChildMenuItem) {
      this.setRootMenuToStorage(this.selectedChildMenuItem);
    }
    this.folderIsEmpty = false;
    if (this.selectedChildMenuItem) {
      const observables = this.getObservables(this.selectedChildMenuItem);
      forkJoin(observables).subscribe({
        next: results => {
          this.userMenuItems = [];
          if (results) {
            this.displayNodeChildren(results, selectedMenuItem);
          }
        }, error: error => {
          this.hierarchyIsLoading = false;
          this.notificationService.error(this.errorGettingMenuItems, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    }
  }

  private displayNodeChildren(results: any, selectedMenuItem: MenuItem): void {
    this.setChildrenAndUpdateNode(results, selectedMenuItem);
    if (selectedMenuItem.id) {
      this.leftMenuService.getUserMenuItems(selectedMenuItem.id, selectedMenuItem.isMachine).subscribe({
        next: userMenuItems => {
          this.setNodeUserMenuItems(selectedMenuItem, userMenuItems);
          this.showNodeUserMenuItems = true;
          this.expandNodeInProgress = false;
          this.displayFirstPage(selectedMenuItem);
          this.hierarchyIsLoading = false;
        }, error: error => {
          this.notificationService.error(this.errorGettingMenuItems, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    } else {
      this.hierarchyIsLoading = false;
    }
  }

  private openPageInsideNode(selectedMenuItem: MenuItem): void {
    this.hierarchyIsLoading = true;
    this.childMenuItemsToDisplay = [];
    if (!selectedMenuItem || !selectedMenuItem.parentId) {
      return;
    }

    this.leftMenuService.getParent(selectedMenuItem.parentId, true).subscribe({
      next: menuItemParent => {
        this.selectedChildMenuItem = Object.assign(new MenuItem(), this.utilsService.getObjectDeepCopy(menuItemParent));
        if (this.selectedChildMenuItem) {
          this.setRootMenuToStorage(this.selectedChildMenuItem);
        }
        this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, selectedMenuItem.id);
        this.folderIsEmpty = false;
        if (this.selectedChildMenuItem) {
          const observables = this.getObservables(this.selectedChildMenuItem);
          forkJoin(observables).subscribe({
            next: results => {
              this.userMenuItems = [];
              if (results) {
                this.expandNodeAndOpenPage(results, selectedMenuItem);
              }
            }, error: error => {
              this.hierarchyIsLoading = false;
              this.notificationService.error(this.errorGettingMenuItems, error, this.CURRENT_PAGE_NAME, true);
            }
          });
        }
      }, error: error => {
        console.log(error);
      }
    });
  }

  private expandNodeAndOpenPage(results: any, selectedMenuItem: MenuItem): void {
    const userMenuItems: UserMenuItem[] = results[1];
    const filteredNodes = userMenuItems.filter(m => String(m.id) === String(selectedMenuItem.nodeId));
    let node: MenuItem = new MenuItem();
    if (filteredNodes && filteredNodes.length > 0) {
      node = Object.assign(new MenuItem(), filteredNodes[0]);
      this.storageService.writeValueToIndexedDb(this.SELECTED_NODE_KEY, node);
    }
    delete selectedMenuItem.nodeId;
    const machine = selectedMenuItem.menuItemParent;
    selectedMenuItem.menuItemParent = Object.assign(new MenuItem(), node);
    selectedMenuItem.menuItemParent.menuItemParent = machine;
    this.setChildrenAndUpdateNode(results, node);
    this.setNodeContent(selectedMenuItem);
  }

  private setNodeContent(selectedMenuItem: MenuItem): void {
    if (this.expandedNodeId) {
      this.leftMenuService.getUserMenuItems(this.expandedNodeId, false).subscribe({
        next: nodeUserMenuItems => {
          if (selectedMenuItem.menuItemParent) {
            this.setNodeUserMenuItems(selectedMenuItem.menuItemParent, nodeUserMenuItems);
          }
          this.showNodeUserMenuItems = true;
          this.expandNodeInProgress = false;
          this.setSelectedPageId(selectedMenuItem.id);
          const commands = this.leftMenuService.getCommands(selectedMenuItem, this.firebaseUserId, this.interactiveDataAnalysisMenuItem);
          this.leftMenuService.navigateToPage(commands);
          this.hierarchyIsLoading = false;
        }, error: error => {
          this.notificationService.error(this.errorGettingMenuItems, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    } else {
      this.hierarchyIsLoading = false;
    }
  }

  private setChildrenAndUpdateNode(results: any[], selectedMenuItem: MenuItem) {
    if (results.length > 0 && this.selectedChildMenuItem) {
      this.setBaseMenuItems(this.selectedChildMenuItem, results[0]);
    }
    if (results.length > 1 && this.selectedChildMenuItem) {
      this.setUserMenuItems(this.selectedChildMenuItem, results[1]);
    }
    this.setChildMenuItemsToDisplay();
    this.checkIfFolderIsEmpty();
    this.expandedNode = selectedMenuItem;
    this.expandedNodeId = selectedMenuItem.id;
    this.nodeUserMenuItems = [];
  }

  private openPageInsideMachine(selectedMenuItem: MenuItem): void {
    this.hierarchyIsLoading = true;
    this.childMenuItemsToDisplay = [];
    const commands = this.leftMenuService.getCommands(selectedMenuItem, this.firebaseUserId, this.interactiveDataAnalysisMenuItem);
    this.leftMenuService.navigateToPage(commands);
    if (selectedMenuItem && selectedMenuItem.menuItemParent && selectedMenuItem.menuItemParent.id) {
      const isParentMachine: boolean = this.getIsParentMachine(selectedMenuItem);
      this.leftMenuService.getParent(selectedMenuItem.menuItemParent.id, isParentMachine).subscribe({
        next: menuItemParent => {
          this.selectedChildMenuItem = Object.assign(new MenuItem(), this.utilsService.getObjectDeepCopy(menuItemParent));
          if (this.selectedChildMenuItem) {
            this.selectedChildMenuItem.menuItemParent = selectedMenuItem.menuItemParent?.menuItemParent;
            this.selectedChildMenuItem.icon = this.designService.getIcon(this.selectedChildMenuItem.type);
            this.setRootMenuToStorage(this.selectedChildMenuItem);
          }
          this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, selectedMenuItem.id);
          this.folderIsEmpty = false;
          this.setMachineContentAndOpenPage(selectedMenuItem);
        }, error: error => {
          console.log(error);
        }
      });
    }
  }

  private getIsParentMachine(selectedMenuItem: MenuItem) {
    if (selectedMenuItem.menuItemParent?.isMachine !== undefined) {
      return selectedMenuItem.menuItemParent?.isMachine;
    }
    return selectedMenuItem.menuItemParent?.type === MenuItemType.MACHINE;
  }

  private setMachineContentAndOpenPage(selectedMenuItem: MenuItem): void {
    if (this.selectedChildMenuItem) {
      const observables = this.getObservables(this.selectedChildMenuItem);
      forkJoin(observables).subscribe({
        next: results => {
          this.userMenuItems = [];
          if (results) {
            if (results.length > 0 && this.selectedChildMenuItem) {
              this.setBaseMenuItems(this.selectedChildMenuItem, results[0]);
            }
            if (results.length > 1 && this.selectedChildMenuItem) {
              this.setUserMenuItems(this.selectedChildMenuItem, results[1]);
            }
            this.setChildMenuItemsToDisplay();
            this.setSelectedPageId(selectedMenuItem.id);
            this.hierarchyIsLoading = false;
          }
        }, error: error => {
          this.hierarchyIsLoading = false;
          this.notificationService.error(this.errorGettingMenuItems, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    }
  }

  private listenMenuNotifications(): void {
    this.subscription = this.menuNotificationService.currentMenuNotification
    .subscribe((message: MenuNotification) => {
      if (this.rootMenuKeyIsValid) {
        this.refreshMenu(message);
      }
    });
  }

  private listenClickOnDashboard(): void {
    this.clickOnDashboardSubscription = this.menuNotificationService.clickOnDashboardNotification
    .subscribe((menuItem: MenuItem) => {
      if (menuItem.id) {
        this.onMenuItemSelectedFromOutside(menuItem);
      }
    });
  }

  private listenClickOnBreadcrumb(): void {
    this.clickOnBreadcrumbSubscription = this.menuNotificationService.clickOnBreadcrumbNotification
    .subscribe((menuItem: MenuItem) => {
      if (menuItem.id) {
        this.setActiveGroupName(menuItem);
        this.onMenuItemSelectedFromOutside(menuItem);
      }
    });
  }

  private setActiveGroupName(menuItem: MenuItem) {
    if (menuItem.type === MenuItemType.MACHINE_GROUP) {
      this.storageService.writeValueToIndexedDb('activeGroupName', menuItem.name);
    }
  }

  private listenRedirectToInteractiveDataAnalysis(): void {
    this.redirectToInteractiveDataAnalysisSubscription = this.menuNotificationService.redirectToInteractiveDataAnalysis
    .subscribe((menuItem: MenuItem) => {
      if (menuItem.id) {
        this.onMenuItemSelectedFromOutside(menuItem);
      }
    });
  }

  private listenMachinePageSelection(): void {
    this.machinePageSelectionSubscription = this.menuNotificationService.machinePageSelection
    .subscribe(selection => {
      if (selection?.machine?.id && selection.page?.id) {
        this.selectMachinePageWithoutNavigation(selection.machine, selection.page);
      }
    });
  }

  private selectMachinePageWithoutNavigation(machineMenuItem: MenuItem, pageMenuItem: MenuItem): void {
    const selectedMachine = Object.assign(new MenuItem(), this.utilsService.getObjectDeepCopy(machineMenuItem));
    this.setRootMenuToStorage(selectedMachine);
    this.selectedChildMenuItem = selectedMachine;
    this.selectedChildMenuItem.favoriteToggle = false;
    this.childMenuItems = [];
    this.baseMenuItems = [];
    this.userMenuItems = [];
    this.childMenuItemsToDisplay = [];
    this.showNodeUserMenuItems = false;
    this.expandedNodeId = null;
    this.nodeUserMenuItems = [];
    this.hierarchyIsLoading = true;
    this.setChildren(selectedMachine, false, pageMenuItem.id);
  }

  private listenRedirectToGroupStatusPage(): void {
    this.menuNotificationService.redirectToGroupStatusPage
    .subscribe((menuItem: MenuItem) => {
      if (menuItem.id) {
        const commands = this.leftMenuService.getCommands(menuItem, this.firebaseUserId, undefined);
        this.leftMenuService.navigateToPage(commands);
      }
    });
  }

  private listenForUpdatedUrl(): void {
    this.updateUrlSubscription = this.updateUrlService.updateUrlMenuNotification
    .subscribe((pageItem: PageItem) => {
      this.childMenuItems.forEach(item => {
        if (String(item.id) === String(pageItem.id)) {
          item.url = pageItem.url;
        }
      });
    });
  }

  private generateAndBroadcastRoutes(selectedMenuItem: MenuItem) {
    const routes = this.breadcrumbService.generateRoutes(selectedMenuItem);
    this.breadcrumbService.broadcastRoutes(routes);
  }

  private getPathRecursive(selectedMenuItem: MenuItem) {
    if (selectedMenuItem.id === this.actualRootMenuItem?.id || selectedMenuItem.menuItemParent?.id === this.actualRootMenuItem?.id) {
      return;
    }
    if (!selectedMenuItem.menuItemParent && selectedMenuItem.id) {
      const parentId = this.getMachineGroupParentItemId(selectedMenuItem);
      if (parentId) {
        this.leftMenuService.getParent(parentId, false).subscribe({
          next: menuItemParent => {
            selectedMenuItem.menuItemParent = Object.assign(new MenuItem(), menuItemParent);
            selectedMenuItem.parentId = menuItemParent.id;
            if (selectedMenuItem.menuItemParent) {
              selectedMenuItem.menuItemParent.icon = this.designService.getIcon(selectedMenuItem.menuItemParent.type);
              this.getPathRecursive(selectedMenuItem.menuItemParent);
            }
          }, error: error => {
            console.log(error);
          }
        });
      }
    }
  }

  private getMachineGroupParentItemId(selectedMenuItem: MenuItem) {
    if (!selectedMenuItem.id) {
      return '';
    }
    if (selectedMenuItem.type === MenuItemType.MACHINE) {
      return selectedMenuItem.id.split('_')[0];
    } else if (selectedMenuItem.type === MenuItemType.MACHINE_GROUP) {
      return selectedMenuItem.parentId;
    } else {
      return '';
    }
  }

}
